import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Heart, Eye, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { Avatar } from "@/components/PostCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { toast } from "sonner";

function VideoTile({ video, onOpen }) {
  return (
    <motion.button initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onClick={() => onOpen(video)}
      data-testid="video-tile" className="nu-card overflow-hidden text-left group">
      <div className="aspect-video relative overflow-hidden bg-black">
        {video.thumb_url ? <img src={mediaUrl(video.thumb_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full bg-gradient-to-br from-violet-600/40 to-black" />}
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-violet-500/90 flex items-center justify-center glow-violet"><Play className="w-6 h-6 text-white ml-1" /></span>
        </div>
      </div>
      <div className="p-4">
        <p className="font-bold truncate">{video.title}</p>
        <div className="mt-2 flex items-center gap-3">
          <Avatar artist={video.artist} size={28} />
          <span className="text-sm text-zinc-400 truncate flex items-center gap-1 flex-1">{video.artist?.artist_name}<VerifiedBadge show={video.artist?.is_subscribed} size={12} /></span>
          <span className="text-xs text-zinc-500 font-mono-nu flex items-center gap-1 shrink-0"><Eye className="w-3 h-3" /> {video.views || 0}</span>
        </div>
      </div>
    </motion.button>
  );
}

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const openVideo = (v) => {
    setActive(v);
    api.post(`/videos/${v.id}/view`).then(({ data }) => {
      setVideos((prev) => prev.map((x) => (x.id === v.id ? { ...x, views: data.views } : x)));
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/videos").then(({ data }) => setVideos(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Music <span className="text-gradient">Videos</span></h1>
      <p className="text-zinc-400 mt-2">Cinematic visuals from NEXT UP artists.</p>

      {loading ? <p className="text-zinc-500 text-center py-16 font-mono-nu">loading…</p>
        : videos.length === 0 ? <p className="text-zinc-500 text-center py-16">No videos yet. Upload the first one!</p>
        : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
            {videos.map((v) => <VideoTile key={v.id} video={v} onOpen={openVideo} />)}
          </div>
        )}

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="video-theater">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <video src={mediaUrl(active.video_url)} poster={mediaUrl(active.thumb_url)} controls autoPlay
                className="w-full rounded-xl bg-black" data-testid="theater-video" />
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-xl">{active.title}</h2>
                  <Link to={`/artist/${active.artist?.id}`} className="text-sm text-emerald-400">{active.artist?.artist_name}</Link>
                  {active.description && <p className="mt-2 text-sm text-zinc-400">{active.description}</p>}
                </div>
                <button onClick={() => setActive(null)} className="text-zinc-400 hover:text-white shrink-0"><X className="w-6 h-6" /></button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
